import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, AlertTriangle, ShoppingCart, Clock, ClipboardCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../lib/DataContext";
import { api, errMsg } from "../lib/api";
import { PageHeader, TierBadge, StockBar, Qty, Panel, isLow, TIER_NAMES } from "../components/shared";
import AdjustDialog from "../components/AdjustDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const Stat = ({ label, value, accent, testId }) => (
  <Panel className="p-5" data-testid={testId}>
    <div className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{label}</div>
    <div className={`font-display text-4xl font-extrabold mt-2 ${accent || ""}`}>{value}</div>
  </Panel>
);

export function ReconcileBox({ it }) {
  const { refreshAll } = useData();
  const [busy, setBusy] = useState(false);
  const on = !!it.needs_reconciliation;
  const toggle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await api.put(`/items/${it.id}/reconcile`, { flag: !on }); await refreshAll(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={`reconcile-checkbox-${it.sku}`} onClick={toggle} disabled={busy} aria-pressed={on} title="Needs Reconciliation"
      className={`h-9 px-2 flex items-center gap-1.5 rounded-md border font-mono text-[10px] font-bold transition-colors ${on ? "border-[#A78BFA] bg-[#A78BFA]/15 text-[#C4B5FD]" : "border-[#334155] text-[#6B7280] hover:text-white"}`}>
      <span className={`w-4 h-4 rounded-sm border grid place-items-center ${on ? "bg-[#A78BFA] border-[#A78BFA] text-[#090D16]" : "border-[#6B7280]"}`}>{on && "✓"}</span>RECON
    </button>
  );
}

function ItemCard({ it, onClick, i }) {
  const low = isLow(it);
  return (
    <div role="button" tabIndex={0} data-testid={`item-card-${it.sku}`} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()} style={{ animationDelay: `${Math.min(i, 20) * 25}ms` }}
      className={`rise cursor-pointer text-left p-5 rounded-md border bg-[#111827] hover:bg-[#1F293D] transition-colors active:scale-[0.99] ${it.needs_reconciliation ? "border-[#A78BFA]/70" : low ? "border-[#F59E0B]/60" : "border-[#1E293B]"}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><TierBadge tier={it.tier} />
        {low && <span data-testid={`low-stock-tag-${it.sku}`} className="flex items-center gap-1 font-mono text-[10px] font-bold text-[#F59E0B]"><AlertTriangle className="w-3.5 h-3.5" />LOW</span>}</div>
        <ReconcileBox it={it} />
      </div>
      <div className="font-semibold leading-tight min-h-[2.5rem]">{it.name}</div>
      <div className="font-mono text-[11px] text-[#6B7280] mt-1">{it.sku} · {it.category}</div>
      <div className="flex items-end justify-between mt-4 mb-2">
        <Qty v={it.stock} unit={it.unit} className={`text-3xl font-bold ${it.stock <= 0 ? "text-[#EF4444]" : low ? "text-[#F59E0B]" : "text-white"}`} />
        <span className="font-mono text-[11px] text-[#6B7280] text-right">
          {it.on_order_qty > 0 && <span className="block text-[#38BDF8]" data-testid={`on-order-tag-${it.sku}`}>+{it.on_order_qty} ON ORDER</span>}
          PAR {it.par_level}
        </span>
      </div>
      <StockBar item={it} />
    </div>
  );
}

export default function Dashboard() {
  const { items, loading, ordering, plans } = useData();
  const [q, setQ] = useState("");
  const [tier, setTier] = useState(0);
  const [cat, setCat] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);
  const [sel, setSel] = useState(null);

  const cats = useMemo(() => [...new Set(items.filter((i) => !tier || i.tier === tier).map((i) => i.category))].sort(), [items, tier]);
  const filtered = items.filter((i) => (!tier || i.tier === tier) && (cat === "all" || i.category === cat) && (!lowOnly || isLow(i)) &&
    (!q || `${i.name} ${i.sku} ${i.category}`.toLowerCase().includes(q.toLowerCase())));
  const lowCount = items.filter(isLow).length;
  const selItem = sel ? items.find((i) => i.id === sel) : null;
  const recon = items.filter((i) => i.needs_reconciliation).length;

  return (
    <div>
      <PageHeader kicker="// INVENTORY OVERVIEW" title="Stock Dashboard" />
      {ordering?.due_soon > 0 && (
        <Link to="/ordering/suggested" data-testid="dashboard-ordering-required"
          className={`mb-6 flex items-center gap-3 p-4 rounded-md border-2 font-semibold transition-colors hover:bg-[#172033] ${ordering.overdue ? "border-[#EF4444] text-[#EF4444]" : "border-[#38BDF8] text-[#38BDF8]"}`}>
          <ShoppingCart className="w-5 h-5 shrink-0" />
          <span className="font-display font-extrabold">ORDERING REQUIRED</span>
          <span className="text-sm text-[#9CA3AF] font-normal">{ordering.due_soon} item{ordering.due_soon > 1 ? "s" : ""} at or near reorder point{ordering.overdue ? ` · ${ordering.overdue} overdue` : ""}</span>
          <span className="ml-auto font-mono text-xs">VIEW SUGGESTED POs →</span>
        </Link>
      )}
      {(ordering?.late_pos > 0 || ordering?.due_soon_pos > 0) && (
        <Link to="/ordering?filter=late" data-testid="dashboard-late-pos"
          className={`mb-6 flex items-center gap-3 p-4 rounded-md border-2 font-semibold transition-colors hover:bg-[#172033] ${ordering.late_pos ? "border-[#EF4444] text-[#EF4444]" : "border-[#F59E0B] text-[#F59E0B]"}`}>
          <Clock className="w-5 h-5 shrink-0" />
          <span className="font-display font-extrabold">{ordering.late_pos ? "LATE POs" : "POs DUE SOON"}</span>
          <span className="text-sm text-[#9CA3AF] font-normal">{ordering.late_pos} past supplier lead time{ordering.due_soon_pos ? ` · ${ordering.due_soon_pos} due within 2 days` : ""}</span>
          <span className="ml-auto font-mono text-xs">CHASE SUPPLIERS →</span>
        </Link>
      )}
      {recon > 0 && (
        <Link to="/reconciliation" data-testid="dashboard-reconciliation-required"
          className="mb-6 flex items-center gap-3 p-4 rounded-md border-2 font-semibold transition-colors hover:bg-[#172033] border-[#A78BFA] text-[#C4B5FD]">
          <ClipboardCheck className="w-5 h-5 shrink-0" />
          <span className="font-display font-extrabold">RECONCILIATION REQUIRED</span>
          <span className="text-sm text-[#9CA3AF] font-normal" data-testid="dashboard-reconciliation-count">{recon} item{recon > 1 ? "s" : ""} flagged for a stock count review</span>
          <span className="ml-auto font-mono text-xs">REVIEW →</span>
        </Link>
      )}
      {plans?.delayed > 0 && (
        <Link to="/schedule/planning" data-testid="dashboard-plan-delays"
          className="mb-6 flex items-center gap-3 p-4 rounded-md border-2 font-semibold transition-colors hover:bg-[#172033] border-[#FB923C] text-[#FB923C]">
          <CalendarClock className="w-5 h-5 shrink-0" />
          <span className="font-display font-extrabold">PRODUCTION PLAN DELAYS</span>
          <span className="text-sm text-[#9CA3AF] font-normal" data-testid="dashboard-plan-delays-count">{plans.delayed} plan{plans.delayed > 1 ? "s" : ""} · {plans.missed_steps} milestone{plans.missed_steps === 1 ? "" : "s"} past Start / Order By and not covered</span>
          <span className="ml-auto font-mono text-xs">REVIEW PLANS →</span>
        </Link>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3].map((t) => <Stat key={t} testId={`stat-tier-${t}`} label={`TIER ${t} · ${TIER_NAMES[t].toUpperCase()}`} value={items.filter((i) => i.tier === t).length} />)}
        <Stat testId="stat-low-stock" label="BELOW PAR" value={lowCount} accent={lowCount ? "text-[#F59E0B]" : "text-[#10B981]"} />
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input data-testid="inventory-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, category…"
            className="w-full h-14 pl-12 pr-4 bg-[#0F1623] border border-[#1E293B] rounded-md focus:outline-none focus:border-[#F59E0B] text-base" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="inventory-category-select" className="h-14 md:w-56 bg-[#0F1623] border-[#1E293B]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#172033] border-[#334155]">
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {[0, 1, 2, 3].map((t) => (
          <button key={t} data-testid={`inventory-tier-filter-${t}`} onClick={() => { setTier(t); setCat("all"); }}
            className={`h-12 px-5 rounded-md text-sm font-semibold border transition-colors ${tier === t ? "bg-white text-[#090D16] border-white" : "border-[#334155] text-[#9CA3AF] hover:text-white"}`}>
            {t ? `Tier ${t} · ${TIER_NAMES[t]}` : "All Tiers"}
          </button>
        ))}
        <button data-testid="inventory-low-filter" onClick={() => setLowOnly(!lowOnly)}
          className={`h-12 px-5 rounded-md text-sm font-semibold border flex items-center gap-2 ${lowOnly ? "bg-[#F59E0B] text-[#090D16] border-[#F59E0B]" : "border-[#F59E0B]/50 text-[#F59E0B]"}`}>
          <AlertTriangle className="w-4 h-4" /> Below par only
        </button>
      </div>
      {loading ? <div className="font-mono text-[#6B7280]">LOADING…</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="inventory-grid">
          {filtered.map((it, i) => <ItemCard key={it.id} it={it} i={i} onClick={() => setSel(it.id)} />)}
          {!filtered.length && <div className="text-[#6B7280] font-mono col-span-full" data-testid="inventory-empty">NO ITEMS MATCH</div>}
        </div>
      )}
      <AdjustDialog item={selItem} onClose={() => setSel(null)} />
    </div>
  );
}
